import { useMutation } from '@tanstack/react-query';
import { signupApi } from '@/services/api';
import { toast } from 'react-hot-toast';
import { debug } from '@/utils/debug';
import { LoadingSpinner } from '@/components/ui';

interface EmailVerificationFailedModalProps {
  isOpen: boolean;
  onClose: () => void;
  email: string;
  memberUuid: string;
  onResendSuccess?: () => void;
}

export function EmailVerificationFailedModal({
  isOpen,
  onClose,
  email,
  memberUuid,
  onResendSuccess,
}: EmailVerificationFailedModalProps) {
  const resendMutation = useMutation({
    mutationFn: () => signupApi.resendVerificationEmail({ member_uuid: memberUuid }),
    onSuccess: (response) => {
      debug.log('Resend verification email response:', response);
      if (response.success) {
        toast.success('Verification email sent successfully! Please check your inbox.', {
          duration: 5000,
        });

        // Call the success callback if provided (e.g., to show the pending modal)
        if (onResendSuccess) {
          onResendSuccess();
        } else {
          // If no callback, just close this modal
          onClose();
        }
      } else {
        toast.error(response.message || 'Failed to resend verification email. Please try again.', {
          duration: 5000,
        });
      }
    },
    onError: (error: Error) => {
      debug.error('Resend verification email failed:', error);
      toast.error('Failed to resend verification email. Please contact support.', {
        duration: 5000,
      });
    },
  });

  const handleResend = () => {
    resendMutation.mutate();
  };

  const handleContactSupport = () => {
    // Open email client with pre-filled support email
    const subject = encodeURIComponent('Email Verification Issue');
    const body = encodeURIComponent(
      `Hello,\n\nI'm having trouble receiving the email verification for my account.\n\nEmail: ${email}\nMember UUID: ${memberUuid}\n\nPlease help me complete my account setup.\n\nThank you!`
    );
    window.location.href = `mailto:support@flouri.ai?subject=${subject}&body=${body}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        {/* Error Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold text-gray-900 text-center mb-2">
          Email Verification Failed
        </h2>

        {/* Description */}
        <div className="text-gray-600 text-center mb-6 space-y-2">
          <p>
            Your account was created successfully, but we couldn't send the verification email to:
          </p>
          <p className="font-semibold text-gray-900">{email}</p>
          <p className="text-sm">
            You'll need to verify your email before you can access your account.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleResend}
            disabled={resendMutation.isPending}
            className="w-full px-6 py-3 bg-blue-800 text-white rounded-md hover:bg-blue-900 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium transition-colors"
          >
            {resendMutation.isPending ? (
              <span className="flex items-center justify-center">
                <LoadingSpinner size="md" variant="white" className="-ml-1 mr-2" label="Sending email" />
                Sending...
              </span>
            ) : (
              'Resend Verification Email'
            )}
          </button>

          <button
            onClick={handleContactSupport}
            className="w-full px-6 py-3 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 font-medium transition-colors"
          >
            Contact Support
          </button>

          <button
            onClick={onClose}
            className="w-full px-6 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors"
          >
            Close
          </button>
        </div>

        {/* Help Text */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            If you continue to have issues, please contact us at{' '}
            <a
              href="mailto:support@flouri.ai"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              support@flouri.ai
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
